import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../features/admin/hooks/usePermissions';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { getRoleBottomNavItems, BottomBarItem } from './navConfig';
import { useTranslation } from '../../i18n';

interface MobileBottomNavProps {
  onOpenDrawer?: () => void;
}

/**
 * MobileBottomNav — Fixed bottom navigation bar with role-based icons and labels.
 * Visible only on mobile/tablet viewports (< 1024px).
 * Fully adheres to safe-area-inset-bottom, PWA standalone display, and virtual keyboard detection.
 */
export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onOpenDrawer }) => {
  const { isTenantAdmin, isPlatformAdmin, can } = usePermissions();
  const { branding } = useTenantBranding();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const getBottomNavLabel = (item: BottomBarItem): string => {
    const map: Record<string, string> = {
      super_admin_tenants: 'nav.organizations',
      super_admin_users: 'nav.users',
      super_admin_simulations: 'nav.simulations',
      super_admin_reports: 'nav.reports',
      super_admin_more: 'nav.menu',
      tenant_admin_home: 'nav.home',
      tenant_admin_simulations: 'nav.simulations',
      tenant_admin_learners: 'nav.learners',
      tenant_admin_reports: 'nav.reports',
      tenant_admin_menu: 'nav.menu',
      learner_home: 'nav.home',
      learner_simulations: 'nav.simulations',
      learner_results: 'nav.my_results',
      learner_profile: 'nav.profile',
    };
    const key = map[item.id] || `nav.${item.id}`;
    return t(key, undefined, item.label);
  };

  // Detect virtual keyboard opening to prevent obscuring mobile inputs
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height;
        // Keyboard typically takes > 150px
        setIsKeyboardOpen(heightDiff > 150);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  const mobileConfig = branding?.mobileNavConfig;

  // If tenant admin explicitly disabled mobile footer, don't render
  if (mobileConfig?.showMobileFooter === false) {
    return null;
  }

  // If virtual keyboard is open, hide bottom nav so form fields are not obscured
  if (isKeyboardOpen) {
    return null;
  }

  // Do not render bottom nav inside the live Simulation Room to protect audio/mic controls
  if (location.pathname.includes('/run')) {
    return null;
  }

  // Check tenant governance for learner report access
  const learnerCanViewReport = branding?.governancePolicy?.learnerCanViewOwnResultReport !== false;

  const items: BottomBarItem[] = getRoleBottomNavItems({
    isPlatformAdmin,
    isTenantAdmin,
    can,
    learnerCanViewReport,
    onOpenProfileDrawer: onOpenDrawer,
  });

  const handleItemClick = (e: React.MouseEvent, item: BottomBarItem) => {
    if (item.path === '#menu' || item.path === '#profile') {
      e.preventDefault();
      onOpenDrawer?.();
      return;
    }

    if (item.path.startsWith('/dashboard#simulations')) {
      e.preventDefault();
      if (location.pathname === '/dashboard') {
        const el = document.getElementById('simulations-list-container') || document.querySelector('.dashboard-simulations-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.location.hash = 'simulations';
        }
      } else {
        navigate('/dashboard#simulations');
      }
    }
  };

  return (
    <nav
      className="mobile-bottom-nav"
      aria-label="Mobile Navigation"
      role="navigation"
    >
      <div className="mobile-bottom-nav__container">
        {items.map((item) => {
          const active = item.isActive(location.pathname);
          const Icon = item.icon;
          const isTrigger = item.path.startsWith('#');
          const displayLabel = getBottomNavLabel(item);

          return (
            <Link
              key={item.id}
              to={isTrigger ? location.pathname : item.path}
              onClick={(e) => handleItemClick(e, item)}
              className={`mobile-bottom-nav__item ${active ? 'active' : ''}`}
              aria-current={active ? 'page' : undefined}
              aria-label={displayLabel}
              tabIndex={0}
            >
              <span className="mobile-bottom-nav__icon-wrapper">
                <Icon size={20} className="mobile-bottom-nav__svg" />
                {active && <span className="mobile-bottom-nav__glow" />}
              </span>
              <span className="mobile-bottom-nav__label">{displayLabel}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
